/**
 * Markets — one symbol, real data only.
 *
 *   price, bid/ask, day stats  ← live feed (WebSocket L1), REST snapshot as fallback
 *   previous close, 52w, avg vol ← /stocks/:sym/overview
 *   candles                     ← /stocks/:sym/candles
 *   order book + trade tape     ← live feed (MBP + trades)
 *
 * Nothing is simulated. A figure we do not have is simply not shown.
 */
import { useState } from 'react';
import { Button, Card, Delta, TickerValue } from '../../components/UI';
import { MarketClock } from '../../components/MarketClock';
import { CandleChart } from './CandleChart';
import { OrderBookCard, TapeCard } from './MarketDepth';
import { OrderTicket } from './OrderTicket';
import { WorkingOrders } from './WorkingOrders';
import { SymbolSelector } from './SymbolSelector';
import { useBreakpoint } from '../../lib/useBreakpoint';
import { useStockCandles, useStockOverview, useStockSnapshot, type CandleRange } from '../../lib/hooks';
import { addWatchlistItem, removeWatchlistItem, useWatchlists } from '../../lib/dataHooks';
import { useLiveFeed } from '../../lib/liveFeed';
import { getMarketStatus } from '../../lib/marketHours';

interface Props {
  symbol?: string;
  onBack: () => void;
  onChangeSymbol: (sym: string) => void;
}

const RANGES: CandleRange[] = ['1D', '1W', '1M', '3M', '1Y', '5Y'];
const pos = (n: number | null | undefined): number | undefined => (n !== null && n !== undefined && Number.isFinite(n) && n > 0 ? n : undefined);

export const StockDetail = ({ symbol = 'ENGROH', onBack, onChangeSymbol }: Props) => {
  const sym = symbol.toUpperCase();
  const [range, setRange] = useState<CandleRange>('1D');
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const mainCols = isMobile ? '1fr' : isTablet ? '1fr 1fr' : 'minmax(0, 2.4fr) minmax(0, 1fr) minmax(0, 1fr)';
  const pagePad = isMobile ? '16px 16px 60px' : '24px 32px 80px';

  const live = useLiveFeed(sym);
  const snapshot = useStockSnapshot(sym, 5000);
  const overview = useStockOverview(sym);
  const candlesQ = useStockCandles(sym, range);

  const l1 = live.l1;
  const marketOpen = getMarketStatus() === 'open';
  // Live push first, 5-second REST snapshot as the fallback.
  const price = pos(l1?.last) ?? pos(snapshot?.last);
  const bid = pos(l1?.bid) ?? pos(snapshot?.bid);
  const ask = pos(l1?.ask) ?? pos(snapshot?.ask);
  const open = pos(l1?.open) ?? pos(snapshot?.open);
  const high = pos(l1?.high) ?? pos(snapshot?.high);
  const low = pos(l1?.low) ?? pos(snapshot?.low);
  const volume = pos(l1?.volume) ?? pos(snapshot?.volume);
  const trades = pos(l1?.trades) ?? pos(snapshot?.trades);
  const vwap = pos(l1?.average) ?? pos(snapshot?.vwap);
  // The feed's previous close is the exchange's adjusted reference price.
  const prevClose = pos(l1?.close) ?? pos(overview?.prevClose);
  const dayChange = price !== undefined && prevClose !== undefined ? price - prevClose : undefined;
  const dayChangePct = dayChange !== undefined && prevClose ? (dayChange / prevClose) * 100 : undefined;

  const streaming = live.socket === 'open' && !!l1 && marketOpen;
  const badge = price === undefined ? 'NO DATA' : streaming ? 'LIVE' : marketOpen ? 'DELAYED' : 'MARKET CLOSED';
  const badgeTitle = streaming ? 'Streaming from the exchange feed' : marketOpen ? 'Refreshing every 5 seconds' : 'Last price of the most recent session';

  // "1D" means the latest session: the API returns the last 24 hours, which
  // would otherwise open the chart with yesterday afternoon's bars.
  const allCandles = candlesQ.candles ?? [];
  const pktDay = (ms: number) => new Date(ms + 5 * 3600 * 1000).toISOString().slice(0, 10);
  const lastDay = allCandles.length ? pktDay(allCandles[allCandles.length - 1].tsMs) : '';
  const candles = range === '1D' ? allCandles.filter((c) => pktDay(c.tsMs) === lastDay) : allCandles;

  return (
    <div style={{ maxWidth: 1440, margin: '0 auto', padding: pagePad }} className="tk-fade-in">
      <button
        onClick={onBack}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', padding: '4px 0', color: 'var(--tk-ink-3)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', marginBottom: 14 }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="m15 18-6-6 6-6" />
        </svg>
        Markets{overview?.sector ? ` · ${overview.sector}` : ''}
      </button>

      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div
              style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--tk-accent)', color: 'var(--tk-accent-ink)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 17, letterSpacing: '0.02em' }}
            >
              {sym.slice(0, 2)}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em' }}>{sym}</h1>
                <SymbolSelector currentSym={sym} onPick={onChangeSymbol} />
                <span
                  title={badgeTitle}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', background: streaming ? 'var(--tk-up-soft)' : 'var(--tk-surface-2)', color: streaming ? 'var(--tk-up)' : 'var(--tk-ink-3)', borderRadius: 4, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', fontFamily: 'var(--tk-mono)' }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: 99, background: streaming ? 'var(--tk-up)' : 'var(--tk-ink-3)', animation: streaming ? 'tk-pulse 2s ease-in-out infinite' : undefined }} />
                  {badge}
                </span>
                <MarketClock />
              </div>
              <div style={{ fontSize: 13, color: 'var(--tk-ink-3)', marginTop: 2 }}>{overview?.name ?? 'Pakistan Stock Exchange'}</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginTop: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, color: 'var(--tk-ink-3)', fontWeight: 500 }}>PKR</span>
            <span style={{ fontSize: 42, lineHeight: 1, fontWeight: 600, letterSpacing: '-0.03em' }}>
              {price !== undefined ? <TickerValue value={price} decimals={2} big /> : <span className="mono">—</span>}
            </span>
            {dayChangePct !== undefined && <Delta value={dayChangePct} size="lg" />}
            {dayChange !== undefined && (
              <span className="mono" style={{ fontSize: 14, fontWeight: 600, color: dayChange >= 0 ? 'var(--tk-up)' : 'var(--tk-down)' }}>
                {dayChange >= 0 ? '+' : ''}
                {dayChange.toFixed(2)} today
              </span>
            )}
            {bid !== undefined && ask !== undefined && (
              <span className="mono" style={{ fontSize: 12, color: 'var(--tk-ink-3)', fontWeight: 500 }}>
                bid {bid.toFixed(2)} · ask {ask.toFixed(2)}
              </span>
            )}
          </div>
        </div>

        <WatchButton sym={sym} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: mainCols, gap: 20, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0, gridColumn: isTablet ? '1 / -1' : 'auto' }}>
          <Card padding={20}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 4, padding: 4, background: 'var(--tk-surface-2)', borderRadius: 'var(--tk-radius-sm)', border: '1px solid var(--tk-line)' }}>
                {RANGES.map((r) => (
                  <button
                    key={r}
                    onClick={() => setRange(r)}
                    style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, letterSpacing: '0.02em', fontFamily: 'var(--tk-mono)', color: range === r ? 'var(--tk-ink)' : 'var(--tk-ink-3)', background: range === r ? 'var(--tk-surface)' : 'transparent', border: 'none', borderRadius: 7, cursor: 'pointer', boxShadow: range === r ? 'var(--tk-shadow-sm)' : 'none' }}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <span className="mono" style={{ fontSize: 11, color: 'var(--tk-ink-3)' }}>
                {candles.length ? `${candles.length} ${candlesQ.interval === '1m' ? 'one-minute' : 'daily'} bars` : ''}
              </span>
            </div>
            {candles.length >= 2 ? (
              <CandleChart
                height={360}
                intraday={candlesQ.interval === '1m'}
                times={candles.map((c) => c.tsMs)}
                candles={candles.map((c) => ({ open: c.open, close: c.close, hi: c.high, lo: c.low, volume: c.volume }))}
              />
            ) : (
              <div style={{ height: 360, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--tk-ink-3)', fontSize: 13 }}>
                {candlesQ.loading ? 'Loading…' : range === '1D' ? 'No bars for today yet.' : 'No price history for this range.'}
              </div>
            )}
          </Card>

          <Card padding={24}>
            <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }}>Overview</h3>
            <Overview
              price={price}
              open={open}
              high={high}
              low={low}
              prevClose={prevClose}
              volume={volume}
              trades={trades}
              vwap={vwap}
              bid={bid}
              ask={ask}
              bidQty={pos(l1?.bidQty)}
              askQty={pos(l1?.askQty)}
              yearLow={pos(overview?.yearLow)}
              yearHigh={pos(overview?.yearHigh)}
              avgVol={pos(overview?.avgVol30d)}
              isMobile={isMobile}
            />
          </Card>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
          <OrderBookCard live={live} marketOpen={marketOpen} />
          <TapeCard live={live} marketOpen={marketOpen} />
        </div>

        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 20 }}>
          {price !== undefined ? (
            <>
              {/* Re-mount per symbol so the ticket's limit price starts at that symbol's market price. */}
              <OrderTicket key={sym} stock={{ sym, price }} />
              <WorkingOrders sym={sym} />
            </>
          ) : (
            <Card padding={24}>
              <div style={{ fontSize: 13, color: 'var(--tk-ink-3)' }}>The order ticket opens once {sym} has a price.</div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

/** Adds the symbol to / removes it from the user's default watchlist. */
const WatchButton = ({ sym }: { sym: string }) => {
  const wl = useWatchlists(0);
  const [busy, setBusy] = useState(false);
  const list = wl.data?.watchlists.find((w) => w.isDefault) ?? wl.data?.watchlists[0];
  if (!list) return null;
  const watching = list.items.includes(sym);
  return (
    <Button
      variant={watching ? 'subtle' : 'ghost'}
      icon="star"
      size="md"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await (watching ? removeWatchlistItem(list.id, sym) : addWatchlistItem(list.id, sym));
          await wl.refetch();
        } finally {
          setBusy(false);
        }
      }}
    >
      {watching ? 'Watching' : 'Watch'}
    </Button>
  );
};

interface OverviewProps {
  price?: number;
  open?: number;
  high?: number;
  low?: number;
  prevClose?: number;
  volume?: number;
  trades?: number;
  vwap?: number;
  bid?: number;
  ask?: number;
  bidQty?: number;
  askQty?: number;
  yearLow?: number;
  yearHigh?: number;
  avgVol?: number;
  isMobile: boolean;
}

const Overview = (p: OverviewProps) => {
  const f = (n?: number, d = 2) => (n === undefined ? undefined : n.toLocaleString('en-PK', { minimumFractionDigits: d, maximumFractionDigits: d }));
  const big = (n?: number) => {
    if (n === undefined) return undefined;
    if (n >= 1e9) return (n / 1e9).toFixed(2) + 'B';
    if (n >= 1e6) return (n / 1e6).toFixed(2) + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
    return Math.round(n).toLocaleString('en-PK');
  };
  // Only figures we actually have; an unknown one is left out, not dashed.
  const stats = (
    [
      ['Open', f(p.open)],
      ['Prev close', f(p.prevClose)],
      ['High', f(p.high)],
      ['Low', f(p.low)],
      ['Volume', big(p.volume)],
      ['Avg volume (30d)', big(p.avgVol)],
      ['VWAP', f(p.vwap)],
      ['Trades today', p.trades !== undefined ? Math.round(p.trades).toLocaleString('en-PK') : undefined],
      ['Best bid', p.bid !== undefined ? `${f(p.bid)}${p.bidQty ? ` × ${big(p.bidQty)}` : ''}` : undefined],
      ['Best ask', p.ask !== undefined ? `${f(p.ask)}${p.askQty ? ` × ${big(p.askQty)}` : ''}` : undefined],
      ['Spread', p.bid !== undefined && p.ask !== undefined ? f(p.ask - p.bid) : undefined],
      ['Traded value', p.volume !== undefined && p.vwap !== undefined ? big(p.volume * p.vwap) : undefined],
    ] as [string, string | undefined][]
  ).filter((s): s is [string, string] => s[1] !== undefined);

  const dayBar = p.price !== undefined && p.low !== undefined && p.high !== undefined && p.high > p.low;
  const yearBar = p.price !== undefined && p.yearLow !== undefined && p.yearHigh !== undefined && p.yearHigh > p.yearLow;

  if (stats.length === 0 && !dayBar && !yearBar) {
    return <div style={{ fontSize: 13, color: 'var(--tk-ink-3)' }}>No market data for this symbol yet.</div>;
  }
  return (
    <div>
      {(dayBar || yearBar) && (
        <div style={{ display: 'grid', gridTemplateColumns: p.isMobile || !(dayBar && yearBar) ? '1fr' : '1fr 1fr', gap: 28, marginBottom: 26 }}>
          {dayBar && <RangeBar label="Day's range" lo={p.low!} hi={p.high!} at={p.price!} />}
          {yearBar && <RangeBar label="52-week range" lo={p.yearLow!} hi={p.yearHigh!} at={p.price!} />}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: p.isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', gap: '22px 28px' }}>
        {stats.map(([label, value]) => (
          <div key={label} style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--tk-ink-3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
            <div className="mono" style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const RangeBar = ({ label, lo, hi, at }: { label: string; lo: number; hi: number; at: number }) => {
  const pct = Math.max(0, Math.min(100, ((at - lo) / (hi - lo)) * 100));
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--tk-ink-3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>{label}</div>
      <div style={{ position: 'relative', height: 6, background: 'var(--tk-surface-2)', borderRadius: 3, marginBottom: 8 }}>
        <div
          style={{ position: 'absolute', left: `${pct}%`, top: -3, transform: 'translateX(-50%)', width: 12, height: 12, borderRadius: 999, background: 'var(--tk-accent)', boxShadow: '0 0 0 3px var(--tk-surface)', transition: 'left 0.5s var(--tk-ease)' }}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--tk-mono)', fontSize: 12, color: 'var(--tk-ink-3)' }}>
        <span>{lo.toFixed(2)}</span>
        <span>{hi.toFixed(2)}</span>
      </div>
    </div>
  );
};
